import React, { useEffect, useState } from "react";
import { Plus } from "lucide-react";
import { taskDetailApi } from "../../backend/client";
import { Button } from "../../components/ui/button";
import { Checkbox } from "../../components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";

const DocumentChecklistForm = ({ projectId, onClose, onSave }) => {
  const [documents, setDocuments] = useState([]);
  const [newDocName, setNewDocName] = useState("");

  useEffect(() => {
    const fetchDocuments = async () => {
      if (!projectId) return;

      const { data, error } = await taskDetailApi.list("task-documents", {
        project_id: projectId,
      });

      if (error) {
        console.error("Error fetching documents:", error.message);
      } else {
        setDocuments(data || []);
      }
    };

    fetchDocuments();
  }, [projectId]);

  const handleAddDocument = async () => {
    if (!newDocName.trim()) return;

    const { data, error } = await taskDetailApi.create("task-documents", {
      project_id: projectId,
      doc_name: newDocName,
    });

    if (error) {
      console.error("Error adding document:", error.message);
    } else {
      setDocuments((previous) => [...previous, data]);
      setNewDocName("");
    }
  };

  const handleToggleComplete = async (docId, currentValue) => {
    const { error } = await taskDetailApi.update("task-documents", docId, {
      is_complete: !currentValue,
    });

    if (error) {
      console.error("Error updating document status:", error.message);
      return;
    }

    setDocuments((previous) =>
      previous.map((document) =>
        document.id === docId
          ? { ...document, is_complete: !currentValue }
          : document,
      ),
    );
  };

  const handleSave = () => {
    onSave(documents);
    onClose();
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose?.()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Documents Checklist</DialogTitle>
          <DialogDescription>
            Track required documents for this project.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-2 sm:grid-cols-[1fr_auto]">
          <Input
            value={newDocName}
            onChange={(event) => setNewDocName(event.target.value)}
            placeholder="Add new document"
          />
          <Button type="button" onClick={handleAddDocument}>
            <Plus className="h-4 w-4" />
            Add
          </Button>
        </div>

        <div className="max-h-72 space-y-2 overflow-y-auto pr-1">
          {documents.length === 0 ? (
            <div className="rounded-md border border-dashed p-8 text-center text-sm text-muted-foreground">
              No documents yet.
            </div>
          ) : (
            documents.map((document) => (
              <label
                key={document.id}
                className="flex items-center justify-between gap-3 rounded-md border bg-muted/20 p-3"
              >
                <span
                  className={
                    document.is_complete
                      ? "break-words text-sm text-muted-foreground line-through"
                      : "break-words text-sm font-medium"
                  }
                >
                  {document.doc_name}
                </span>
                <Checkbox
                  checked={!!document.is_complete}
                  onCheckedChange={() =>
                    handleToggleComplete(document.id, document.is_complete)
                  }
                />
              </label>
            ))
          )}
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button type="button" onClick={handleSave}>
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default DocumentChecklistForm;
